/**
 * Profile Sharing Service
 *
 * Handles sending profile information and data exchange through chat messages
 * Implements the standardized data architecture for reliable profile data exchange
 */
import { sendMessage } from './chatService';
import {
  getProfileData,
  saveReceivedProfileData,
  getAnonData,
  saveAnonData,
  getConnectionMetadata,
  saveConnectionMetadata,
  saveReceivedAnonData,
} from '../storage/userDataStorage';
// Queue system removed — sendMessage is now DB-first
import { getProfileUserId } from '../../utils/identityUtils';

// Re-export these functions for use in components that have been updated to use profileService
export {
  getProfileData,
  saveReceivedProfileData,
  getAnonData,
  saveAnonData,
  getConnectionMetadata,
  saveConnectionMetadata,
  saveReceivedAnonData,
};

// Message types for data transfer using request-response pattern
export const MESSAGE_TYPES = {
  PROFILE_DATA_REQUEST: 'profile_data_request', // Anonymous user requests profile data
  PROFILE_DATA_RESPONSE: 'profile_data_response', // Profile user responds with data
  ANON_DATA_REQUEST: 'anon_data_request', // Profile user requests anonymous data
  ANON_DATA_RESPONSE: 'anon_data_response', // Anonymous user responds with data
  CONTACT: 'contact', // Contact information message
  CHAT: 'chat', // Regular chat message
  PRESENCE: 'presence', // Presence update message
  UPGRADE_TO_PROFILE: 'upgrade_to_profile', // Anonymous user upgraded to profile user (legacy)
  QR_PRESENCE: 'qr_presence', // Profile user presence on QR screen
  METADATA_UPDATED: 'metadata_updated', // Connection metadata changed (venue selection, date/time edit, etc.)
  PROFILE_EXCHANGE: 'profile_exchange', // PXP: Profile user sends their data to another Profile user via in-app scanner
};

/**
 * Send profile data in response to a request
 * Uses sendMessage (DB-first) for reliable delivery
 * IMPORTANT: This function also saves connectionMetadata locally for the SENDER
 * @param {string} chatRoomId - The chat room ID
 * @param {Object} data - Object containing profileData and/or connectionMetadata
 * @returns {Promise<{success: boolean}>} Result
 */
export const sendProfileDataResponse = async (chatRoomId, data) => {
  if (!chatRoomId) {
    console.error('[PROFILE] Missing chatRoomId for profile data response');
    return { success: false, error: 'Missing chatRoomId' };
  }

  if (!data || !data.profileData) {
    console.error('[PROFILE] Missing profileData for profile data response');
    return { success: false, error: 'Missing profileData' };
  }

  try {
    const profileData = data.profileData;
    const connectionMetadata = data.connectionMetadata || null;
    const senderProfileUserId = data.senderProfileUserId || null;

    // Save connectionMetadata locally for the SENDER
    if (connectionMetadata) {
      await saveConnectionMetadata(chatRoomId, connectionMetadata);
    }

    // Send via sendMessage (DB-first + WebSocket hint)
    const message = {
      type: MESSAGE_TYPES.PROFILE_DATA_RESPONSE,
      sender: 'profile',
      senderId: getProfileUserId(),
      profileData,
      connectionMetadata,
      senderProfileUserId,
      timestamp: new Date().toISOString(),
    };

    const result = await sendMessage(chatRoomId, message);

    if (result.error) {
      console.error(`[PROFILE] sendProfileDataResponse failed for ${chatRoomId}:`, result.error);
      return { success: false, error: result.error };
    }

    console.log(`[PROFILE] Profile data response sent for ${chatRoomId}`);
    return { success: true };
  } catch (error) {
    console.error(`[PROFILE] Error sending profile data response for ${chatRoomId}:`, error);
    return { success: false, error: error.message };
  }
};
